import log from 'loglevel';
import { StoreApi } from 'zustand';

import {
  MessageDialogCloseHandler,
  MessageDialogDiscardHandler,
  MessageDialogOpenParams,
  MessageDialogState,
  MessageDialogStore,
} from './types';
import { HandlerButton } from '../../hooks/UserCommunications/useViewMessageComposerHandler';
import { isSnapshotSyncPending } from '../ComposeSnapshotIndex/syncGate';
import { useComposeSnapshotIndexStore } from '../ComposeSnapshotIndex/useComposeSnapshotIndexStore';
import { getComposeSnapshot } from '../utils/composeSnapshotCache';

type ZustandSet = StoreApi<MessageDialogStore>['setState'];
type ZustandGet = StoreApi<MessageDialogStore>['getState'];

export const INITIAL_STATE: MessageDialogState = {
  isOpen: false,
  dialogKey: 0,
  messageId: undefined,
  draftId: undefined,
  selectedHandlerButton: HandlerButton.UNSELECTED,
  inboxEntryId: undefined,
  scheduledEmail: undefined,
  composeToRecipients: undefined,
  closeHandler: undefined,
  discardHandler: undefined,
  aiAssisted: false,
  isAboveModal: false,
  isNewMessageExpanded: true,
  prefill: undefined,
  onSent: undefined,
  restoreSnapshotSessionId: undefined,
  missingSnapshotNoticeCount: 0,
};

let openChain: Promise<void> = Promise.resolve();

export const handleReportMissingSnapshot = (
  set: ZustandSet,
  get: ZustandGet,
  sessionId: string,
): void => {
  useComposeSnapshotIndexStore.getState().removeEntry(sessionId);
  set({ missingSnapshotNoticeCount: get().missingSnapshotNoticeCount + 1 });
};

const isSnapshotMissing = async (sessionId: string): Promise<boolean> => {
  const { userId } = useComposeSnapshotIndexStore.getState();
  if (!userId) {
    return false;
  }
  return !(await getComposeSnapshot(userId, sessionId));
};

// A sync in flight never blocks the open: the compose shows the snapshot at once and adopts the draft when it lands.
// Returns undefined when the snapshot no longer exists anywhere, so nothing must open.
const resolveSnapshotParams = async (
  set: ZustandSet,
  get: ZustandGet,
  params?: MessageDialogOpenParams,
): Promise<MessageDialogOpenParams | undefined> => {
  const sessionId = params?.restoreSnapshotSessionId;
  if (!params || !sessionId || isSnapshotSyncPending(sessionId)) {
    return params;
  }
  if (await isSnapshotMissing(sessionId)) {
    // A sync that finished before this open saved the snapshot; it is not lost.
    const syncedDraftId =
      useComposeSnapshotIndexStore.getState().syncedDraftIdBySessionId[
        sessionId
      ];
    if (syncedDraftId) {
      return {
        ...params,
        draftId: syncedDraftId,
        restoreSnapshotSessionId: undefined,
      };
    }
    handleReportMissingSnapshot(set, get, sessionId);
    return undefined;
  }
  return params;
};

// A row whose snapshot the open compose already restored must keep that compose, not replace it with a second one.
const isHeldByOpenCompose = (
  get: ZustandGet,
  sessionId: string | undefined,
): boolean =>
  Boolean(
    sessionId &&
      get().isOpen &&
      useComposeSnapshotIndexStore.getState().claimedSourceSessionIds[
        sessionId
      ],
  );

const performOpen = async (
  set: ZustandSet,
  get: ZustandGet,
  requestedParams?: MessageDialogOpenParams,
): Promise<void> => {
  if (isHeldByOpenCompose(get, requestedParams?.restoreSnapshotSessionId)) {
    return;
  }
  const params = await resolveSnapshotParams(set, get, requestedParams);
  if (requestedParams?.restoreSnapshotSessionId && !params) {
    return;
  }
  const { isOpen, closeHandler } = get();

  if (isOpen) {
    // False means nothing holds the current content; the close path already toasted why.
    if (closeHandler && (await closeHandler()) === false) {
      return;
    }
  } else {
    handleClose(set, get);
  }

  set({
    isOpen: true,
    dialogKey: get().dialogKey + 1,
    closeHandler: undefined,
    discardHandler: undefined,
    messageId: params?.messageId,
    draftId: params?.draftId,
    selectedHandlerButton:
      params?.selectedHandlerButton ?? HandlerButton.UNSELECTED,
    inboxEntryId: params?.inboxEntryId,
    scheduledEmail: params?.scheduledEmail,
    composeToRecipients: params?.composeToRecipients,
    aiAssisted: params?.aiAssisted ?? false,
    prefill: params?.prefill,
    onSent: params?.onSent,
    restoreSnapshotSessionId: params?.restoreSnapshotSessionId,
  });
};

export const handleOpen = (
  set: ZustandSet,
  get: ZustandGet,
  params?: MessageDialogOpenParams,
): Promise<void> => {
  const chained = openChain.then(() =>
    performOpen(set, get, params).catch((error) => {
      log.error('useMessageDialogStore: failed to open dialog', error);
    }),
  );
  openChain = chained;
  return chained;
};

export const handleClose = (set: ZustandSet, get: ZustandGet) => {
  set({
    ...INITIAL_STATE,
    // Monotonic: a late onClose() from a previous instance must never match a newer one.
    dialogKey: get().dialogKey,
    isAboveModal: get().isAboveModal,
    isNewMessageExpanded: get().isNewMessageExpanded,
    missingSnapshotNoticeCount: get().missingSnapshotNoticeCount,
  });
};

export const handleCloseIfCurrent = (
  set: ZustandSet,
  get: ZustandGet,
  dialogKey: number,
) => {
  if (!get().isOpen || get().dialogKey !== dialogKey) {
    return;
  }
  handleClose(set, get);
};

export const handleCloseIfShowingDraft = async (
  set: ZustandSet,
  get: ZustandGet,
  draftIds: string | string[],
): Promise<void> => {
  const draftIdsArray = Array.isArray(draftIds) ? draftIds : [draftIds];
  const { isOpen, draftId, discardHandler } = get();

  if (!isOpen || !draftId || !draftIdsArray.includes(draftId)) {
    return;
  }

  const discardResult = discardHandler?.();
  handleClose(set, get);

  try {
    await discardResult;
  } catch (error) {
    log.error(
      'useMessageDialogStore: discardHandler failed during closeIfShowingDraft',
      error,
    );
  }
};

export const handleUpdateMessageId = (set: ZustandSet, messageId: string) => {
  set({ messageId });
};

export const handleUpdateDraftId = (set: ZustandSet, draftId: string) => {
  set({ draftId });
};

export const handleRegisterCloseHandler = (
  set: ZustandSet,
  handler: MessageDialogCloseHandler,
) => {
  set({ closeHandler: handler });
};

export const handleUnregisterCloseHandler = (
  set: ZustandSet,
  get: ZustandGet,
  handler: MessageDialogCloseHandler,
) => {
  if (get().closeHandler === handler) {
    set({ closeHandler: undefined });
  }
};

export const handleRegisterDiscardHandler = (
  set: ZustandSet,
  handler: MessageDialogDiscardHandler,
) => {
  set({ discardHandler: handler });
};

export const handleUnregisterDiscardHandler = (
  set: ZustandSet,
  get: ZustandGet,
  handler: MessageDialogDiscardHandler,
) => {
  if (get().discardHandler === handler) {
    set({ discardHandler: undefined });
  }
};

export const handleResetState = (set: ZustandSet, get: ZustandGet) => {
  handleClose(set, get);
  set({ dialogKey: INITIAL_STATE.dialogKey, missingSnapshotNoticeCount: 0 });
};

export const handleSetIsAboveModal = (set: ZustandSet, value: boolean) => {
  set({ isAboveModal: value });
};

export const handleSetNewMessageExpanded = (
  set: ZustandSet,
  value: boolean,
) => {
  set({ isNewMessageExpanded: value });
};
